import { useEffect, useRef, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Chave para armazenar no localStorage se a permissão de localização já foi concedida
const LOCATION_PERMISSION_KEY = "location_permission_granted";

/**
 * Verifica se a permissão de localização foi concedida anteriormente
 */
export function isLocationPermissionGranted() {
  return localStorage.getItem(LOCATION_PERMISSION_KEY) === "true";
}

/**
 * Marca que a permissão de localização foi concedida
 */
export function setLocationPermissionGranted(granted) {
  if (granted) {
    localStorage.setItem(LOCATION_PERMISSION_KEY, "true");
  } else {
    localStorage.removeItem(LOCATION_PERMISSION_KEY);
  }
}

/**
 * Solicita permissão de localização ao navegador (usar apenas uma vez, no aceite dos termos)
 * Retorna true se a permissão foi concedida, false caso contrário
 */
export async function requestLocationPermission() {
  // Se já foi concedida anteriormente, não precisa pedir novamente
  if (isLocationPermissionGranted()) {
    return true;
  }

  // Verificar suporte
  if (!("geolocation" in navigator)) {
    console.debug("Geolocation not supported");
    return false;
  }

  // Verificar API de permissões (mais confiável)
  if ("permissions" in navigator) {
    try {
      const permission = await navigator.permissions.query({ name: "geolocation" });
      if (permission.state === "granted") {
        setLocationPermissionGranted(true);
        return true;
      }
      if (permission.state === "denied") {
        return false;
      }
    } catch (e) {
      // Fallback se a API de permissões não funcionar
    }
  }

  // Solicitar permissão através de getCurrentPosition
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      () => {
        // Permissão concedida
        setLocationPermissionGranted(true);
        resolve(true);
      },
      (error) => {
        // Permissão negada ou erro
        console.debug("Location permission denied:", error.message);
        resolve(false);
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 0 }
    );
  });
}

/**
 * Hook de rastreamento de localização OCULTO para check-in automático em eventos
 * - Não exibe nenhum componente visual
 * - Roda silenciosamente em background
 * - Envia posição periodicamente para o backend
 * - Backend verifica se há eventos ativos e registra presença
 * - NÃO solicita permissão - assume que já foi concedida no aceite dos termos
 */
export function useSilentLocationTracker({ user, enabled = true }) {
  const watchIdRef = useRef(null);
  const intervalRef = useRef(null);
  const lastSentRef = useRef(null);
  
  // Só rastreia se:
  // 1. Hook está habilitado
  // 2. Usuário está logado
  // 3. Usuário aceitou os termos (inclui permissão de localização)
  // 4. Permissão de localização foi concedida no navegador
  const shouldTrack = enabled && 
    user?.id && 
    user?.termos_aceitos === true && 
    user?.permitir_localizacao === true &&
    isLocationPermissionGranted();

  // Enviar posição para o backend (silenciosamente)
  const sendPosition = useCallback(async (latitude, longitude) => {
    // Evitar enviar muitas requisições (máximo 1 a cada 30 segundos)
    const now = Date.now();
    if (lastSentRef.current && (now - lastSentRef.current) < 30000) {
      return;
    }
    
    try {
      await axios.post(`${API}/events/checkin/gps`, {
        latitude,
        longitude,
        pessoa_id: user?.id
      });
      
      lastSentRef.current = now;
    } catch (error) {
      // Silenciosamente ignora erros - não queremos interromper o usuário
      console.debug("Silent GPS check-in:", error.message);
    }
  }, [user?.id]);

  // Handler de posição
  const handlePosition = useCallback((position) => {
    const { latitude, longitude } = position.coords;
    sendPosition(latitude, longitude);
  }, [sendPosition]);

  // Handler de erro (silencioso)
  const handleError = useCallback((error) => {
    // Silenciosamente ignora erros de geolocalização
    console.debug("Geolocation error (silent):", error.message);
  }, []);

  // Iniciar/parar rastreamento
  useEffect(() => {
    if (!shouldTrack) {
      // Limpar se não deve rastrear
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Verificar suporte a geolocalização
    if (!("geolocation" in navigator)) {
      return;
    }

    // Obter posição inicial (sem solicitar nova permissão)
    navigator.geolocation.getCurrentPosition(
      handlePosition,
      handleError,
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 }
    );

    // Rastrear posição continuamente (com baixa precisão para economizar bateria)
    watchIdRef.current = navigator.geolocation.watchPosition(
      handlePosition,
      handleError,
      {
        enableHighAccuracy: false, // Baixa precisão = economia de bateria
        timeout: 30000,
        maximumAge: 60000 // Cache de 1 minuto
      }
    );

    // Também enviar posição a cada 2 minutos como fallback
    intervalRef.current = setInterval(() => {
      navigator.geolocation.getCurrentPosition(
        handlePosition,
        handleError,
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 30000 }
      );
    }, 120000); // 2 minutos

    // Cleanup
    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [shouldTrack, handlePosition, handleError]);

  // Este hook não retorna nada visível - é 100% oculto
  return null;
}
